import { Swords, Shield, Heart, Zap, Hammer, Crosshair, Droplets, Sparkles, Wind, Scissors, ShieldOff, Target, Bug, Skull, Mountain, Star, Flame, Snowflake, Moon, Sun, Biohazard, Wrench, TriangleAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import { CardRoleRow } from "@/components/CardRoleBadge";

const GEAR_RTEXT = { common: "text-zinc-300", uncommon: "text-emerald-300", rare: "text-sky-300", epic: "text-fuchsia-300", legendary: "text-amber-300", goddess: "text-rose-300" };

// Elemental damage types — each attack carries one; cards resist them by rarity.
export const ELEMENT_META = {
  kinetic: { name: "Kinetic", color: "#cbd5e1", Icon: Swords },
  pyro:    { name: "Pyro",    color: "#fb7185", Icon: Flame },
  cryo:    { name: "Cryo",    color: "#67e8f9", Icon: Snowflake },
  volt:    { name: "Volt",    color: "#fde047", Icon: Zap },
  toxin:   { name: "Toxin",   color: "#a3e635", Icon: Biohazard },
  umbral:  { name: "Umbral",  color: "#a78bfa", Icon: Moon },
  radiant: { name: "Radiant", color: "#fcd34d", Icon: Sun },
  astral:  { name: "Astral",  color: "#f0abfc", Icon: Sparkles },
};

export const ABILITIES = {
  heavy_strike: { name: "Heavy Strike", Icon: Hammer, desc: "Deal 170% ATK damage", color: "text-orange-300" },
  pierce: { name: "Piercing Blow", Icon: Crosshair, desc: "120% ATK, ignores DEF", color: "text-cyan-300" },
  drain: { name: "Life Drain", Icon: Droplets, desc: "Deal damage, heal half of it", color: "text-fuchsia-300" },
  mend: { name: "Mend", Icon: Sparkles, desc: "Restore 35% max HP", color: "text-emerald-300" },
};

export const MOVE_ICONS = {
  strike: Swords,
  swift_jab: Wind,
  crushing_blow: Hammer,
  twin_slash: Scissors,
  guard_crush: ShieldOff,
  focus_blast: Target,
  leech_bite: Bug,
  skull_splitter: Skull,
  titan_smash: Mountain,
};

const STAT_META = [
  { key: "atk", Icon: Swords, color: "text-orange-300" },
  { key: "def", Icon: Shield, color: "text-cyan-300" },
  { key: "hp", Icon: Heart, color: "text-emerald-300" },
  { key: "spd", Icon: Zap, color: "text-amber-300" },
];

// Attribute effect keys that actually raise each base stat at mint.
const STAT_ATTR_KEYS = {
  atk: ["atk_pct", "all_pct"],
  def: ["def_pct", "all_pct"],
  hp: ["hp_pct", "all_pct"],
  spd: ["spd_pct", "all_pct"],
};

// Every source touching a stat (buffs + Find-on-Mint traits), with signed amount.
// Positive entries are boosts, negative entries are drawbacks/handicaps.
export function statBoostSources(fighter, statKey) {
  const sources = [];
  for (const b of fighter?.applied_buffs || []) {
    const ap = b.apply || {};
    if (statKey in ap && ap[statKey] !== 0) {
      const p = Math.round(ap[statKey] * 100);
      sources.push({ name: b.name, detail: `${p >= 0 ? "+" : ""}${p}%`, pct: p });
    }
  }
  const keys = STAT_ATTR_KEYS[statKey] || [];
  for (const a of fighter?.attributes || []) {
    const e = a.effects || {};
    let pct = 0;
    for (const k of keys) pct += e[k] || 0;
    if (pct !== 0) {
      const p = Math.round(pct * 100);
      sources.push({ name: a.name, detail: `${p >= 0 ? "+" : ""}${p}%`, pct: p });
    }
  }
  return sources;
}

// A single stat value that turns green ▲ when boosted above its base, or red ▼ when
// dragged below it by a drawback/handicap — with a hover tooltip listing every source.
export function StatPip({ fighter, statKey, current, Icon, color, className = "" }) {
  const base = fighter?.base?.[statKey];
  const changed = base != null && current !== base;
  const up = changed && current > base;
  const down = changed && current < base;
  const sources = changed ? statBoostSources(fighter, statKey) : [];
  const title = changed
    ? `${statKey.toUpperCase()}  ${base} → ${current}` +
      (sources.length ? `\n${up ? "Boosted by:" : "Modified by:"}\n` + sources.map((s) => `• ${s.name} (${s.detail})`).join("\n") : "")
    : undefined;
  const tone = up ? "text-green-400" : down ? "text-red-400" : "";
  return (
    <span
      title={title}
      data-testid={up ? `stat-boosted-${statKey}` : down ? `stat-reduced-${statKey}` : `stat-${statKey}`}
      className={cn("inline-flex items-center gap-1", changed ? `${tone} font-semibold cursor-help` : "", className)}
    >
      <Icon className={cn("h-2.5 w-2.5", up ? "text-green-400" : down ? "text-red-400" : color)} /> {current}
      {up && <span className="text-[7px] leading-none">▲</span>}
      {down && <span className="text-[7px] leading-none">▼</span>}
    </span>
  );
}

// Compact ATK/DEF/HP/SPD chip row + every special ability the card carries
// (natural + gear-granted). Previously showed only the singular `.ability`
// which caused equipped relics to visually "override" the natural ability —
// the backend already stacks correctly via `abilities_detail`.
export function ArenaStatChips({ arena, className = "", compact = false }) {
  if (!arena) return null;
  // Prefer the full list; fall back to the singular `.ability` for any older
  // fighter view (e.g. mid-battle snapshots that predate the fix).
  const abilityIds = arena.abilities && arena.abilities.length
    ? arena.abilities
    : (arena.ability ? [arena.ability] : []);
  const sources = Object.fromEntries((arena.abilities_detail || []).map((d) => [d.id, d.source]));
  const val = (key) => (key === "hp" ? arena.hp : arena[key]);
  return (
    <div className={`flex flex-wrap items-center gap-1 ${className}`} data-testid="arena-stat-chips">
      {STAT_META.map(({ key, Icon, color }) => {
        const current = val(key);
        const baseV = arena.base?.[key];
        const up = baseV != null && current > baseV;
        const down = baseV != null && current < baseV;
        const boostSources = (up || down) ? statBoostSources(arena, key) : [];
        const title = (up || down)
          ? `${key.toUpperCase()}  ${baseV} → ${current}` +
            (boostSources.length ? `\n${up ? "Boosted by:" : "Modified by:"}\n` + boostSources.map((s) => `• ${s.name} (${s.detail})`).join("\n") : "")
          : undefined;
        const tone = up ? "text-green-400 border-green-400/40" : down ? "text-red-400 border-red-400/40" : "text-white/80 border-white/10";
        return (
          <span key={key} title={title} data-testid={up ? `chip-boosted-${key}` : down ? `chip-reduced-${key}` : undefined}
            className={cn("inline-flex items-center gap-0.5 rounded bg-black/50 border px-1 py-0.5 font-mono text-[9px]",
              tone, (up || down) ? "cursor-help" : "")}>
            <Icon className={cn("h-2.5 w-2.5", up ? "text-green-400" : down ? "text-red-400" : color)} /> {current}{up ? "▲" : down ? "▼" : ""}
          </span>
        );
      })}
      {abilityIds.map((id) => {
        const ab = ABILITIES[id];
        if (!ab) return null;
        const src = sources[id];
        const isGear = src && src !== "NATURAL";
        return (
          <span
            key={id}
            data-testid={`ability-chip-${id}`}
            className={`inline-flex items-center gap-0.5 rounded bg-black/50 border px-1 py-0.5 font-mono text-[9px] ${ab.color} ${isGear ? "border-amber-300/50" : "border-white/10"}`}
            title={`${ab.name} — ${ab.desc}${src ? ` (${src})` : ""}`}
          >
            <ab.Icon className="h-2.5 w-2.5" />{!compact && ` ${ab.name}`}
          </span>
        );
      })}
    </div>
  );
}

// Named attribute chips (hover for the exact effect).
export function traitPct(a) {
  const e = a?.effects || {};
  let best = 0;
  for (const v of Object.values(e)) {
    if (typeof v === "number" && Math.abs(v) > Math.abs(best)) best = v;
  }
  return Math.round(best * 100);
}

export function isMintTrait(a) {
  return !!(a?.locked || a?.mint || a?.name === "Cavern Mint");
}

// Trait text colour: mint/locked traits get a distinct gold italic; regular traits
// are tinted by the magnitude of their strongest effect %.
export function traitColor(a) {
  if (isMintTrait(a)) return { color: "#fcd34d", fontStyle: "italic", textShadow: "0 0 6px rgba(250,204,21,0.55)" };
  const p = traitPct(a);
  if (p < 0) return { color: "#7f1d1d" };
  if (p < 10) return { color: "#ffffff" };
  if (p < 15) return { color: "#2dd4bf" };
  if (p < 25) return { color: "#3b82f6" };
  if (p < 35) return { color: "#22c55e" };
  if (p < 50) return { color: "#fb923c" };
  if (p < 70) return { color: "#a855f7" };
  if (p < 100) return { color: "#cd7f32" };
  if (p < 120) return { color: "#e2e8f0", textShadow: "0 0 6px rgba(255,255,255,0.85)" };
  return { color: "#fde68a", textShadow: "0 0 9px rgba(245,158,11,0.95)" };
}

export function AttributeChips({ attributes, className = "", detailed = false }) {
  if (!attributes?.length) return null;
  if (detailed) {
    // Full readout — one trait per row with the numeric description that ships
    // in `a.desc` (e.g. "+15% ATK" or "Critical hits deal +55% damage"). Mint /
    // locked traits keep their gold styling so they read as permanent.
    return (
      <div className={`flex flex-col gap-1 ${className}`} data-testid="attribute-chips">
        {attributes.map((a) => {
          const mint = isMintTrait(a);
          return (
            <div
              key={a.name}
              data-testid={`trait-${a.name.replace(/\s+/g, "-").toLowerCase()}`}
              className={`rounded-lg px-2 py-1.5 border ${mint ? "bg-amber-400/[0.08] border-amber-300/40" : "bg-white/[0.03] border-white/12"}`}
            >
              <div className="flex items-center gap-1.5" style={traitColor(a)}>
                <Star className="h-3 w-3 shrink-0" />
                <span className="font-mono text-[11px] font-semibold tracking-wide truncate">{a.name}</span>
              </div>
              {a.desc && (
                <div className="mt-0.5 text-[10px] leading-snug text-white/65">{a.desc}</div>
              )}
            </div>
          );
        })}
      </div>
    );
  }
  return (
    <div className={`flex flex-wrap items-center gap-1 ${className}`} data-testid="attribute-chips">
      {attributes.map((a) => (
        <span
          key={a.name}
          title={`${a.desc}${isMintTrait(a) ? " (locked mint trait)" : ""}`}
          style={traitColor(a)}
          className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-mono text-[9px] cursor-help border ${isMintTrait(a) ? "bg-amber-400/10 border-amber-300/40" : "bg-white/5 border-white/15"}`}
        >
          <Star className="h-2.5 w-2.5" /> {a.name}
        </span>
      ))}
    </div>
  );
}

// Full readable profile: stats + attributes (with effects) + attack moves.
// Used on the card detail page and the War Room slots.
// Combat modifier metadata — friendly names + colors for every mod the engine rolls.
export const MOD_META = {
  crit: { name: "Crit Chance", color: "#fbbf24" },
  crit_dmg: { name: "Crit Damage", color: "#f97316" },
  lifesteal: { name: "Lifesteal", color: "#f43f5e" },
  regen: { name: "Regen", color: "#34d399" },
  thorns: { name: "Thorns", color: "#a3e635" },
  dodge: { name: "Dodge", color: "#67e8f9" },
  berserk: { name: "Berserk", color: "#fb7185" },
  armor_pen: { name: "Armor Pierce", color: "#c084fc" },
  execute: { name: "Execute", color: "#f87171" },
  multistrike: { name: "Multi-Strike", color: "#38bdf8" },
  shield_pct: { name: "Shield", color: "#93c5fd" },
  counter: { name: "Counter", color: "#fde047" },
  last_stand: { name: "Last Stand", color: "#fca5a5" },
  tough: { name: "Toughness", color: "#a8a29e" },
  rage: { name: "Rage", color: "#ef4444" },
  dot: { name: "DoT Power", color: "#84cc16" },
  revive: { name: "Revive", color: "#e879f9" },
};

// Combat mods grid — every rolled/geared modifier (crit, lifesteal, regen…) as a %.
export function ModsGrid({ mods, className = "" }) {
  const entries = Object.entries(mods || {}).filter(([k, v]) => MOD_META[k] && Math.abs(v) >= 0.005);
  if (!entries.length) return null;
  const hasDot = entries.some(([k]) => k === "dot");
  return (
    <div className={className} data-testid="mods-grid">
      <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-orange-300/80 mb-1.5">Combat Mods</div>
      <div className="grid grid-cols-2 gap-1.5">
        {entries.map(([k, v]) => {
          const m = MOD_META[k];
          const pct = Math.round(v * 100);
          const neg = pct < 0;
          return (
            <div key={k} data-testid={`mod-${k}`}
              className="flex items-center justify-between gap-1.5 rounded-lg border px-2 py-1.5 min-w-0"
              style={{ borderColor: neg ? "rgba(251,113,133,0.3)" : `${m.color}38`, background: neg ? "rgba(251,113,133,0.06)" : `${m.color}0d` }}>
              <span className="font-mono text-[10px] text-white/70 leading-tight">{m.name}</span>
              <span className="font-mono text-xs font-semibold tabular-nums shrink-0" style={{ color: neg ? "#fb7185" : m.color }}>
                {pct >= 0 ? "+" : ""}{pct}%
              </span>
            </div>
          );
        })}
      </div>
      {hasDot && (
        <div data-testid="dot-arena-disclaimer" className="mt-2 flex items-start gap-1.5 rounded-lg border border-amber-400/25 bg-amber-400/[0.06] px-2 py-1.5">
          <TriangleAlert className="h-3.5 w-3.5 text-amber-300 shrink-0 mt-px" />
          <span className="font-mono text-[9px] leading-snug text-amber-200/85">
            <b className="text-amber-200">DoT Power is disabled in Arena duels</b> — it would be overpowered 1v1. It only ticks in Cavern party delves &amp; races.
          </span>
        </div>
      )}
    </div>
  );
}

export function ArenaProfile({ arena, className = "", card = null, interactive = false, onRoleChanged = null }) {
  if (!arena) return null;
  return (
    <div className={`space-y-2 ${className}`} data-testid="arena-profile">
      {card && <CardRoleRow card={card} interactive={interactive} onChanged={onRoleChanged} />}
      <ArenaStatChips arena={arena} />
      {arena.level_bonus_pct > 0 && arena.pre_level && (
        <div data-testid="level-stat-breakdown" className="rounded-lg border border-emerald-400/25 bg-emerald-400/[0.06] p-2">
          <div className="flex items-center justify-between mb-1.5">
            <span className="font-mono text-[8px] uppercase tracking-[0.3em] text-emerald-300/80">From level-ups</span>
            <span className="font-mono text-[10px] text-emerald-300">+{arena.level_bonus_pct}% · Lv {arena.level}</span>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {STAT_META.map(({ key, Icon, color }) => {
              const pre = arena.pre_level[key];
              const fin = key === "hp" ? arena.hp : arena[key];
              return (
                <div key={key} className="flex items-center justify-between text-[10px] font-mono" data-testid={`level-breakdown-${key}`}>
                  <span className="inline-flex items-center gap-1 text-white/45"><Icon className={cn("h-2.5 w-2.5", color)} />{key.toUpperCase()}</span>
                  <span className="text-white/60">{pre} <span className="text-emerald-300 font-semibold">→ {fin}</span></span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {arena.attributes?.length > 0 && (
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-violet-300/80 mb-1.5">Traits</div>
          <div className="space-y-1.5">
            {arena.attributes.map((a) => {
              const tc = traitColor(a);
              const col = tc.color || "#cbd5e1";
              const mint = isMintTrait(a);
              return (
                <div key={a.name} data-testid="trait-row" className="relative overflow-hidden rounded-lg border px-2.5 py-1.5"
                  style={{ borderColor: `${col}40`, background: `linear-gradient(90deg, ${col}1f, ${col}08 45%, transparent)` }}>
                  <span className="absolute left-0 top-0 h-full w-[3px]" style={{ background: col, boxShadow: `0 0 10px ${col}` }} />
                  <div className="flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-md shrink-0" style={{ background: `${col}22`, border: `1px solid ${col}55` }}>
                      <Star className="h-3 w-3" style={{ color: col }} />
                    </span>
                    <span className="font-mono font-semibold text-[13px] leading-none" style={tc}>{a.name}</span>
                    {mint && <span className="ml-auto text-[8px] uppercase tracking-wider text-amber-200 px-1.5 py-px rounded bg-amber-400/10 border border-amber-300/40">mint</span>}
                  </div>
                  {a.desc && <div className="text-white/60 text-[11px] leading-snug mt-1 pl-7">{a.desc}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {arena.moves?.length > 0 && (
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-cyan-300/80 mb-1.5">Attacks</div>
          <div className="space-y-1.5">
            {arena.moves.map((m) => {
              const el = ELEMENT_META[m.element || "kinetic"] || ELEMENT_META.kinetic;
              const Icon = el.Icon;
              const col = el.color;
              return (
                <div key={m.id} data-testid="attack-row" className="relative overflow-hidden rounded-lg border px-2.5 py-1.5"
                  style={{ borderColor: `${col}40`, background: `linear-gradient(90deg, ${col}1f, ${col}08 45%, transparent)` }}>
                  <span className="absolute left-0 top-0 h-full w-[3px]" style={{ background: col, boxShadow: `0 0 10px ${col}` }} />
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="flex h-5 w-5 items-center justify-center rounded-md shrink-0" style={{ background: `${col}22`, border: `1px solid ${col}55` }} title={`${el.name} damage`}>
                      <Icon className="h-3 w-3" style={{ color: col }} />
                    </span>
                    <span className="font-mono font-semibold text-[13px] leading-none" style={{ color: col }}>{m.name}</span>
                    <span className="ml-auto rounded px-1.5 py-px text-[8px] uppercase tracking-wider" style={{ color: col, background: `${col}22`, border: `1px solid ${col}55` }}>{el.name}</span>
                  </div>
                  {m.desc && <div className="text-white/60 text-[11px] leading-snug mt-1 pl-7 break-words">{m.desc}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {arena.gear?.length > 0 && (
        <div data-testid="arena-gear-bonuses">
          <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-amber-300/80 mb-1.5">From Gear & Items</div>
          <div className="space-y-1.5">
            {arena.gear.map((g, i) => (
              <div key={g.id || `${g.name}-${i}`} className="text-[13px] leading-tight" data-testid={`gear-bonus-${g.slot || g.kind || i}`}>
                <span className={cn("font-mono font-semibold inline-flex items-center gap-1.5", GEAR_RTEXT[g.rarity] || "text-amber-200")}>
                  <Wrench className="h-3.5 w-3.5 shrink-0" /> {g.name}
                  {g.slot && <span className="text-[8px] uppercase tracking-wider opacity-70">· {g.slot}</span>}
                </span>
                {g.desc && g.desc !== "—" && <div className="text-white/60 text-[11px] leading-snug mt-0.5 break-words">{g.desc}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
      <ModsGrid mods={arena.mods} />
      <ResistGrid resist={arena.resist} />
    </div>
  );
}

// Elemental resistance grid — a card's SET defenses by rarity. Green = resist,
// red = weakness, dim = neutral. Each cell hovers for the exact figure.
export function ResistGrid({ resist, className = "", hideLabel = false, size = "sm" }) {
  if (!resist?.length) return null;
  const big = size === "lg";
  return (
    <div className={className} data-testid="resist-grid">
      {!hideLabel && <div className={cn("font-mono uppercase tracking-[0.3em] text-rose-300/70 mb-1", big ? "text-[12px]" : "text-[8px]")}>Elemental Resist</div>}
      <div className={cn("grid grid-cols-4", big ? "gap-1.5" : "gap-1")}>
        {resist.map((r) => {
          const meta = ELEMENT_META[r.element] || ELEMENT_META.kinetic;
          const Icon = meta.Icon;
          const reflect = r.pct > 100;
          const tone = reflect
            ? "text-fuchsia-300 border-fuchsia-400/45 bg-fuchsia-400/[0.10] shadow-[0_0_10px_rgba(240,171,252,0.18)]"
            : r.pct > 0
              ? "text-emerald-300 border-emerald-400/30 bg-emerald-400/[0.06]"
              : r.pct < 0
                ? "text-rose-300 border-rose-400/30 bg-rose-400/[0.07]"
                : "text-white/35 border-white/10 bg-white/[0.02]";
          const tip = reflect
            ? `${meta.name}: immune — reflects ${Math.min(100, r.pct - 100)}% damage back`
            : r.pct >= 100 ? `${meta.name}: fully immune`
            : r.pct > 0 ? `${meta.name}: resists ${r.pct}% damage`
            : r.pct < 0 ? `${meta.name}: WEAK — takes ${Math.abs(r.pct)}% more damage`
            : `${meta.name}: neutral`;
          return (
            <span key={r.element} title={tip} data-testid={`resist-${r.element}`}
              className={cn(
                "inline-flex items-center justify-between rounded border font-mono cursor-help",
                big ? "px-3 py-2 text-[14px] gap-2 min-w-0" : "px-1.5 py-1 text-[9px] gap-1",
                tone
              )}>
              <Icon className={cn("shrink-0", big ? "h-5 w-5" : "h-2.5 w-2.5")} style={{ color: meta.color }} />
              <span className="tabular-nums">{r.pct > 0 ? `+${r.pct}` : r.pct}%</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

export function squadPower(arena) {
  if (!arena) return 0;
  return arena.atk + arena.def + arena.spd + Math.round(arena.hp / 2);
}

export function HpBar({ hp, maxHp, className = "" }) {
  const pct = Math.max(0, Math.min(100, (hp / Math.max(1, maxHp)) * 100));
  const color = pct > 50 ? "bg-emerald-400" : pct > 25 ? "bg-amber-400" : "bg-red-500";
  return (
    <div className={`w-full ${className}`}>
      <div className="h-2 rounded-full bg-black/60 border border-white/10 overflow-hidden">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-0.5 font-mono text-[9px] text-white/60 text-right">{hp} / {maxHp}</div>
    </div>
  );
}
